<!-- Loading State -->
@if (!feeData) {
  <div class="fee-loading">
    <div class="fee-loading-spinner"></div>
    <p>Estimating gas &amp; fees...</p>
  </div>
}

<!-- Fee Spoiler Container -->
@if (feeData) {
  <div class="fee-spoiler-container">
    <!-- Gas Estimation Error Banner -->
    @if (hasGasLimitError) {
      <div class="error-banner">
        <kc-icon iconClass="icon-warning" size="sm" color="var(--orange-20)" />
        <span
          >Gas estimation failed — the transaction may be invalid. Please verify
          it and adjust the gas limit manually.</span
        >
      </div>
    }
    <!-- Fee Toggle Header (Spoiler) -->
    <div class="fee-spoiler-toggle" (click)="toggleFeeSelection()">
      <div class="fee-summary-left">
        <kc-icon iconClass="icon-chain" size="sm" color="var(--kaspa-20)" />
        <span class="fee-label">Total Fee:</span>
        <div class="fee-value">
          @if (totalFeeDisplay() !== undefined) {
            <span>{{ totalFeeDisplay() | number: "1.4-8" }}</span>
            <kc-icon iconClass="icon-kaspa-coin" size="sm" />
          } @else {
            <span class="not-selected">Not selected</span>
          }
        </div>
      </div>
      <div class="fee-spoiler-chevron">
        <kc-icon
          [iconClass]="
            showFeeSelection ? 'icon-chevron-up' : 'icon-chevron-down'
          "
          size="sm"
          color="var(--gray-60)"
        />
      </div>
    </div>
    <!-- Fee Selection Content (Collapsible) -->
    <div
      class="fee-selection-content"
      [@slideDown]="showFeeSelection ? 'open' : 'closed'"
    >
      <div class="fee-selection-inner">
        <!-- Fee Options Grid -->
        <div class="fee-options-grid">
          <div
            class="fee-option-card"
            [class.selected]="selectedOption() === 'low'"
            (click)="selectOption('low')"
          >
            <div class="option-title">Low</div>
            @if (gasFeeOptionToDisplay()) {
              <div class="option-fee">
                <span>{{
                  gasFeeOptionToDisplay()!["low"].display | number: "1.4-8"
                }}</span>
                <kc-icon iconClass="icon-kaspa-coin" size="sm" />
              </div>
            }
            @if (gasFeeOtions()) {
              <div class="option-gwei">
                {{ fromWeiToGwei(gasFeeOtions()!.low) }} Gwei
              </div>
            }
          </div>
          <div
            class="fee-option-card"
            [class.selected]="selectedOption() === 'normal'"
            (click)="selectOption('normal')"
          >
            <div class="option-title">Normal</div>
            @if (gasFeeOptionToDisplay()) {
              <div class="option-fee">
                <span>{{
                  gasFeeOptionToDisplay()!["normal"].display | number: "1.4-8"
                }}</span>
                <kc-icon iconClass="icon-kaspa-coin" size="sm" />
              </div>
            }
            @if (gasFeeOtions()) {
              <div class="option-gwei">
                {{ fromWeiToGwei(gasFeeOtions()!.normal) }} Gwei
              </div>
            }
          </div>
          <div
            class="fee-option-card"
            [class.selected]="selectedOption() === 'priority'"
            (click)="selectOption('priority')"
          >
            <div class="option-title">Priority</div>
            @if (gasFeeOptionToDisplay()) {
              <div class="option-fee">
                <span>{{
                  gasFeeOptionToDisplay()!["priority"].display | number: "1.4-8"
                }}</span>
                <kc-icon iconClass="icon-kaspa-coin" size="sm" />
              </div>
            }
            @if (gasFeeOtions()) {
              <div class="option-gwei">
                {{ fromWeiToGwei(gasFeeOtions()!.priority) }} Gwei
              </div>
            }
          </div>
        </div>
        <!-- Custom Fee -->
        <div class="custom-fee-section">
          <div
            class="custom-fee-card"
            [class.selected]="selectedOption() === 'custom'"
            [class.has-error]="
              selectedOption() === 'custom' && !isCustomFeeValid()
            "
            (click)="selectOption('custom')"
          >
            <div class="custom-fee-header">
              <span class="custom-fee-title">Custom Fee</span>
              <span class="custom-fee-unit">in wei</span>
            </div>
            <div class="custom-fee-input">
              <kc-number-input
                [enforceEthersFormat]="false"
                placeholder="Min 1 wei"
                [(ngModel)]="customFee"
                (valueChange)="selectOption('custom')"
              />
            </div>
            @if (selectedOption() === "custom" && !isCustomFeeValid()) {
              <div class="validation-error">Minimum custom fee is 1 wei</div>
            }
          </div>
        </div>
        <!-- Gas Limit -->
        <div class="estimated-gas-section">
          <div class="custom-fee-card" [class.has-error]="!isGasLimitValid()">
            <div class="custom-fee-header">
              <span class="custom-fee-title">Gas Limit</span>
            </div>
            <div class="custom-fee-input">
              <kc-number-input
                [enforceEthersFormat]="false"
                placeholder="Min 21,000"
                [(ngModel)]="gasLimit"
                (valueChange)="updateGasLimit()"
              />
            </div>
            @if (!isGasLimitValid()) {
              <div class="validation-error">
                Gas limit must be at least 21,000
              </div>
            }
          </div>
        </div>
        <!-- Fee Breakdown -->
        <div class="fee-details-section">
          <div class="fee-detail-row">
            <span class="detail-label">Base Fee</span>
            <div class="detail-value">
              <span>{{ currentBaseFee() | number: "1.4-8" }}</span>
              <kc-icon iconClass="icon-kaspa-coin" size="sm" />
            </div>
          </div>
          @if (gasFeeOtions()) {
            <div class="fee-detail-row">
              <span class="detail-label">Priority Fee</span>
              <div class="detail-value">
                <span
                  >{{
                    fromWeiToGwei(gasFeeOtions()![selectedOption()])
                  }}
                  Gwei</span
                >
              </div>
            </div>
          }
          @if (gasLimit()) {
            <div class="fee-detail-row">
              <span class="detail-label">Gas Limit</span>
              <div class="detail-value">
                <span>{{ gasLimit() | number }}</span>
              </div>
            </div>
          }
          @if (totalFeeDisplay() !== undefined) {
            <div class="fee-detail-row total-row">
              <span class="detail-label">Total Fee</span>
              <div class="detail-value highlight">
                <span>{{ totalFeeDisplay() | number: "1.4-8" }}</span>
                <kc-icon iconClass="icon-kaspa-coin" size="sm" />
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
